/**
 * Reading a progress stream in the browser.
 *
 * The server side of this is `functions/lib/progress.ts`; this is the half that
 * turns those frames back into steps and a terminal outcome.
 *
 * ---------------------------------------------------------------------------
 * Why not `EventSource`
 * ---------------------------------------------------------------------------
 * `EventSource` can only issue a GET, and both search endpoints are POSTs
 * carrying a JSON body (and a `credentials: "include"` session cookie). So the
 * stream is read off a normal `fetch` response body instead. That also means
 * one code path can handle both shapes, which is the next paragraph.
 *
 * ---------------------------------------------------------------------------
 * The fallback is load-bearing, not defensive clutter
 * ---------------------------------------------------------------------------
 * If the response is not an event stream, this reads it as JSON and reports the
 * HTTP status — which covers three real cases, not a hypothetical one:
 *
 *   1. The pre-stream rejections the server deliberately keeps as statuses
 *      (401 not signed in, 400 empty need).
 *   2. A deployment where the edge declined to stream. Streaming from a Pages
 *      Function is verified locally under `wrangler pages dev` but local
 *      `workerd` is not the Cloudflare edge, and treating a local pass as proof
 *      of production behaviour is the exact inference defect this repo keeps
 *      filing (#93, #132, #148). If the edge ever buffers, the shopper loses
 *      the feed and keeps the answer.
 *   3. Any client or test that answers with plain JSON.
 */

/** One step, as it arrives. Mirrors `ProgressStep` in src/lib/shopping/progress.ts. */
export interface ProgressStepView {
  phase: string;
  detail?: string;
  /** True when the step completed by failing. */
  failed?: boolean;
  /**
   * Integers observed at this boundary — `totalResults`, histories fetched, the
   * screening buckets, a wait the token bucket itself reported. Never a share
   * or a projection; see `ProgressFacts` for the rule.
   */
  counts?: Record<string, number>;
  /** Hedged listing sentences, verbatim from `describeSignalEvidence`. */
  evidence?: string[];
  /** Server clock at emission, milliseconds. Display order is arrival order. */
  at?: number;
}

export interface StreamedOutcome {
  /** The status the non-streaming path would have returned. */
  status: number;
  // biome-ignore lint/suspicious/noExplicitAny: response shape varies per endpoint and status
  data: any;
}

/**
 * Raised when the stream ended without a terminal `result`/`error` frame.
 *
 * A truncated stream is not a result, and must not be presented as one. The
 * server always writes exactly one terminal frame — including for an exception,
 * which arrives as `error` with a 500 — so reaching here means the connection
 * itself was lost.
 */
export class IncompleteStreamError extends Error {
  constructor() {
    super("The connection closed before the search finished.");
    this.name = "IncompleteStreamError";
  }
}

/**
 * Reads a response as either a progress stream or a plain JSON body.
 *
 * `onStep` is called once per `step` / `step-failed` frame, in arrival order,
 * as each arrives. Keepalive comment lines are skipped here and can therefore
 * never reach the UI — a keepalive says the socket is alive, not that anything
 * happened, and rendering one as progress would be the timer-driven fake this
 * whole feature refuses to ship.
 */
export async function readStreamedOutcome(
  response: Response,
  onStep: (step: ProgressStepView) => void,
): Promise<StreamedOutcome> {
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("text/event-stream") || response.body === null) {
    // Not every failure arrives as our JSON either — Cloudflare replaces some
    // origin 5xx responses with its own text/plain error page, so parsing
    // unconditionally would throw a JSON syntax error and bury the real status.
    const data = await response.json().catch(() => ({}));
    return { status: response.status, data };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let outcome: StreamedOutcome | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        outcome = consumeFrame(frame, onStep) ?? outcome;
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    // A caller that navigates away mid-stream should not leave the body locked.
    reader.releaseLock();
  }

  // A final frame with no trailing blank line still counts.
  outcome = consumeFrame(buffer, onStep) ?? outcome;

  if (outcome === null) throw new IncompleteStreamError();
  return outcome;
}

/** Parses one SSE frame, returning the outcome if it was a terminal one. */
function consumeFrame(
  frame: string,
  onStep: (step: ProgressStepView) => void,
): StreamedOutcome | null {
  let event = "message";
  const data: string[] = [];

  for (const line of frame.split("\n")) {
    // A comment line. `: keepalive` is the only one we send, and it is
    // deliberately not an event: nothing downstream can render it.
    if (line === "" || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
      continue;
    }
    if (line.startsWith("data:")) data.push(line.slice("data:".length).trimStart());
  }
  if (data.length === 0) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(data.join("\n"));
  } catch {
    // A frame we cannot read is dropped rather than guessed at. The terminal
    // frame is what decides the outcome, and a missing one still throws.
    return null;
  }

  if (event === "step" || event === "step-failed") {
    const step = payload as ProgressStepView;
    onStep({ ...step, ...(event === "step-failed" ? { failed: true } : {}) });
    return null;
  }
  if (event === "result" || event === "error") {
    const terminal = payload as { status?: number; body?: unknown };
    return {
      status: typeof terminal.status === "number" ? terminal.status : 500,
      data: terminal.body ?? {},
    };
  }
  return null;
}
